import { useEffect, useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useSites, useUser } from 'store'
import useFilters from '../store';

const OPERATION_DOCUMENTS = gql`
  query searchAudinDocuments(
    $verbatim: String
    $type: String
    $step: String
    $sf_ids: [ID]
    $offset: Int
    $limit: Int
    $sort: InputSort
  ) {
    documents: searchAudinDocuments(
      verbatim: $verbatim
      type: $type
      step: $step
      sf_ids: $sf_ids
      offset: $offset
      limit: $limit
      sort: $sort
    ) {
      id
      site {
        id
        name
        location {
          latitude
          longitude
        }
        full_address
      }
      step
      type
      organism
      name
      kind
      person_in_charge {
        full_name
        email
      }
      project_manager {
        full_name
        email
      }
      created_at
      updated_at
    }
  }
`;
const LIMIT = 10;

const useDocuments = () => {
  const { t } = useTranslation();
  const [shouldFetchNextPage, setShouldFetchNextPage] = useState(true);
  const [{ selected }] = useSites();
  const [user] = useUser();
  const sfIds = useMemo(
    () => selected ? selected.map(({sf_id}) => sf_id) : [],
    [selected],
  );
  const [{type, step}] = useFilters();

  const queryVariables = useMemo(() => {
    const authorizedTypes = [];
    if (user.roles.documents_audin_audit) authorizedTypes.push('audit');
    if (user.roles.documents_audin_integration) authorizedTypes.push('integration');
    if (user.roles.documents_audin_technical_visit) authorizedTypes.push('technicalvisit');
    if (user.roles.documents_audin_revised_technical_visit) authorizedTypes.push('revisedtechnicalvisit');
    const variables = {
      sf_ids: sfIds, 
      limit: LIMIT, 
      type: type || authorizedTypes.join(','), 
      step 
    };
    return variables;
  }, [sfIds, type, step, user]);  

  const { error, loading, data, refetch, fetchMore, networkStatus } = useQuery(
    OPERATION_DOCUMENTS,
    {
      notifyOnNetworkStatusChange: true,
      variables: queryVariables,
    },
  );
  
  const documents = useMemo(
    () =>
      data
        ? data.documents.map((document) => ({
          site_name: document.site.name,
          kind_name: t(`audin_document.report_kinds.${document.kind}`),
          ...document,
        }))
        : [],
    [data],
  );

  const site = useMemo(() => {
    if (!documents.length) {
      return null;
    }
    return { ...documents[0].site, full_address: documents[0].full_address };
  }, [documents]);

  useEffect(() => {
    refetch(queryVariables);
  }, [queryVariables]);

  return {
    loading: loading && networkStatus !== 3,
    loadingNext: networkStatus === 3,
    shouldFetchNextPage:
      !loading && documents.length >= LIMIT && shouldFetchNextPage,
    error,
    documents,
    site,
    sfIds,
    fetchNextPage: async () => {
      const fetchMoreResult = await fetchMore({
        variables: {
          offset: documents.length,
        },
      });
      if (
        !fetchMoreResult.data.documents.length ||
        fetchMoreResult.data.documents.length < LIMIT
      ) {
        setShouldFetchNextPage(false);
      }
    },
    refetch: async (value) => {
      refetch(value).then(() => setShouldFetchNextPage(true));
    },
  };
};

export default useDocuments;
